<template>
  <Notification :item="item">
    <template #logo>
      <FontAwesomeIcon
        :icon="['fak', 'list']"
        :style="{ height: '16px', width: '16px', color: 'currentColor' }"
        class="text-info-accent"
      />
    </template>
    <template #message>{{ notificationTitle }}</template>
    <template #description>
      <div>
        {{ notificationDescription }}
      </div>
    </template>
    <template v-if="!!report || (!isAdded && !isGaChanged)" #action>
      <BittsButton
        :text="actionText"
        variant="outline"
        size="x-small"
        @click="handleViewList"
      />
    </template>
  </Notification>
</template>

<script setup lang="ts">
import { BittsButton } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

import Notification from '@/components/notifications/Notification.vue';

import { GENERAL_ACCESS_ONLY_INVITED } from '@/constants/reports';
import { useNotificationsStore, useReportsStore } from '@/stores';
import { Notification as NotificationType } from '@/types/notifications';

const { item } = defineProps<{ item: NotificationType }>();

const emit = defineEmits<(e: 'action-clicked', action: string) => void>();

const router = useRouter();
const notificationsStore = useNotificationsStore();
const reportsStore = useReportsStore();

const { reports } = storeToRefs(reportsStore);

const isAdded = computed(() => item.data?.is_added === true);
const isRemoved = computed(() => item.data?.is_removed === true);
const isDeleted = computed(() => item.data?.is_deleted === true);
const isGaChanged = computed(() => item.data?.is_ga_change === true);

const report = computed(() =>
  reports.value.find((r) => r.id === item.data?.report_id),
);

onMounted(async () => {
  if ((isAdded.value || isGaChanged.value) && !report.value) {
    await reportsStore.refreshReportsStore();
  }
});

const notificationTitle = computed(() => {
  if (isGaChanged.value) return `General Access updated`;
  if (isAdded.value) return 'New List Shared';
  if (isRemoved.value) return 'List Sharing Stopped';
  if (isDeleted.value) return 'List Deleted';
  return 'List Updated';
});

const notificationDescription = computed(() => {
  const sharerName = item.data?.sharer_name || 'Someone';
  const reportName = item.data?.report_name || 'a List';

  if (isGaChanged.value) {
    return item.data?.new_access_level === GENERAL_ACCESS_ONLY_INVITED
      ? `Users now cannot see ${reportName} unless explicitly added`
      : `All users in your organization can view ${reportName}`;
  }
  if (isAdded.value) {
    return `${sharerName} shared a new List of accounts on ${reportName}`;
  }
  if (isRemoved.value) {
    return `${reportName} is no longer shared`;
  }
  return `${sharerName} has deleted ${reportName}`;
});

const actionText = computed(() => item.data?.cta_text || 'View List');

async function handleViewList() {
  emit('action-clicked', actionText.value);
  await notificationsStore.markRead({ id: item.id, isRead: true });
  await router.push(
    (isAdded.value || isGaChanged.value) && report.value
      ? {
          name: 'edit_report',
          params: { report_id: report.value.id },
        }
      : {
          name: 'lists',
        },
  );
}
</script>
